// Browser stand-in for react-native-keychain.
//
// Policy (docs/PLAN_web-app.md §4.3): anything the app stores with an
// `accessControl` (biometry-gated on the phone: master key, wrapping secret,
// user salt) lives in module memory only and dies with the tab. Everything
// else (session tokens, device-trust token, rate-limit counters) goes to
// sessionStorage so a page reload inside one browser session survives.
// Nothing here ever touches localStorage.
//
// The API surface mirrors what the app calls: get/set/resetGenericPassword,
// get/set/resetInternetCredentials, and the constant enums.

export const ACCESSIBLE = {
  WHEN_UNLOCKED: 'AccessibleWhenUnlocked',
  AFTER_FIRST_UNLOCK: 'AccessibleAfterFirstUnlock',
  ALWAYS: 'AccessibleAlways',
  WHEN_PASSCODE_SET_THIS_DEVICE_ONLY: 'AccessibleWhenPasscodeSetThisDeviceOnly',
  WHEN_UNLOCKED_THIS_DEVICE_ONLY: 'AccessibleWhenUnlockedThisDeviceOnly',
  AFTER_FIRST_UNLOCK_THIS_DEVICE_ONLY: 'AccessibleAfterFirstUnlockThisDeviceOnly',
} as const;

export const ACCESS_CONTROL = {
  USER_PRESENCE: 'UserPresence',
  BIOMETRY_ANY: 'BiometryAny',
  BIOMETRY_CURRENT_SET: 'BiometryCurrentSet',
  DEVICE_PASSCODE: 'DevicePasscode',
  APPLICATION_PASSWORD: 'ApplicationPassword',
  BIOMETRY_ANY_OR_DEVICE_PASSCODE: 'BiometryAnyOrDevicePasscode',
  BIOMETRY_CURRENT_SET_OR_DEVICE_PASSCODE: 'BiometryCurrentSetOrDevicePasscode',
} as const;

export const AUTHENTICATION_TYPE = {
  DEVICE_PASSCODE_OR_BIOMETRICS: 'AuthenticationWithBiometricsDevicePasscode',
  BIOMETRICS: 'AuthenticationWithBiometrics',
} as const;

export const STORAGE_TYPE = {
  FB: 'FacebookConceal',
  AES: 'KeystoreAESCBC',
  AES_CBC: 'KeystoreAESCBC',
  AES_GCM: 'KeystoreAESGCM',
  AES_GCM_NO_AUTH: 'KeystoreAESGCM_NoAuth',
  RSA: 'KeystoreRSAECB',
} as const;

export const SECURITY_LEVEL = {
  ANY: 'ANY',
  SECURE_SOFTWARE: 'SECURE_SOFTWARE',
  SECURE_HARDWARE: 'SECURE_HARDWARE',
} as const;

export const BIOMETRY_TYPE = {
  TOUCH_ID: 'TouchID',
  FACE_ID: 'FaceID',
  FINGERPRINT: 'Fingerprint',
  FACE: 'Face',
  IRIS: 'Iris',
} as const;

export interface SetOptions {
  service?: string;
  server?: string;
  accessible?: string;
  accessControl?: string;
  authenticationType?: string;
  storage?: string;
  securityLevel?: string;
}

export interface GetOptions {
  service?: string;
  server?: string;
  authenticationPrompt?: unknown;
}

export interface UserCredentials {
  username: string;
  password: string;
  service: string;
  storage: string;
}

export interface Result {
  service: string;
  storage: string;
}

const DEFAULT_SERVICE = 'default';
const SESSION_PREFIX = 'fk.kc.';

// Services that hold vault key material. The phone caches some of these at
// "medium" sensitivity for keychain-compatibility reasons; on web they are
// memory-only no matter what the caller asks, so the vault key never
// outlives the page (docs/PLAN_web-app.md §4.3). Names match
// src/services/storage/SecureStorage.ts and the internet-credential ids used
// by documentStorageService / letterRecordingStorage.
const MEMORY_ONLY_SERVICES = new Set([
  'MasterRecoveryKey.myfinalkey',
  'WrappingSecretKey.myfinalkey',
  'myfinalkey.user_salt',
  'com.myfinalkey.auth',
]);

// The 30-day device-trust token never reaches the page on web: the API sets
// it as an httpOnly cookie and hands the client the placeholder below
// (backend/src/auth/web-session.ts). The login screen only asks the API to
// check device trust when this service holds something, and sessionStorage
// is per tab, so a fresh tab would never ask and always get the code prompt.
// Reading this service therefore yields the placeholder when nothing is
// stored; the API then checks its cookie, and answers "not trusted" when
// there is none, which costs one request and nothing else.
export const DEVICE_TRUST_SERVICE = 'myfinalkey.device_trust_token';
export const DEVICE_TRUST_PLACEHOLDER = { username: 'myfinalkey', password: 'cookie' };

// Hygiene: a page that ran an earlier build may have left vault material in
// sessionStorage. Remove it on load so nothing memory-only lingers there.
try {
  const stale: string[] = [];
  for (let i = 0; i < window.sessionStorage.length; i += 1) {
    const key = window.sessionStorage.key(i);
    if (!key || !key.startsWith(SESSION_PREFIX)) continue;
    const service = key.slice(SESSION_PREFIX.length);
    if (MEMORY_ONLY_SERVICES.has(service) || service.startsWith('inet:')) stale.push(key);
  }
  stale.forEach((k) => window.sessionStorage.removeItem(k));
} catch {
  // sessionStorage unavailable (e.g. tests without a window); nothing to clean.
}

function isMemoryOnly(service: string, options?: SetOptions | string): boolean {
  if (MEMORY_ONLY_SERVICES.has(service)) return true;
  if (service.startsWith('inet:')) return true; // internet credentials = master-key backups
  return typeof options === 'object' && !!options?.accessControl;
}

// Memory-only tier: entries written with an accessControl.
const memory = new Map<string, { username: string; password: string }>();
// Remember which services were memory-tier so reads never fall through to
// sessionStorage for a key that must not persist.
const memoryServices = new Set<string>();

function serviceOf(options?: SetOptions | GetOptions | string): string {
  if (typeof options === 'string') return options;
  return options?.service ?? options?.server ?? DEFAULT_SERVICE;
}

function readSession(service: string): { username: string; password: string } | null {
  try {
    const raw = window.sessionStorage.getItem(SESSION_PREFIX + service);
    return raw ? (JSON.parse(raw) as { username: string; password: string }) : null;
  } catch {
    return null;
  }
}

function writeSession(service: string, username: string, password: string): void {
  window.sessionStorage.setItem(SESSION_PREFIX + service, JSON.stringify({ username, password }));
}

function removeSession(service: string): void {
  try {
    window.sessionStorage.removeItem(SESSION_PREFIX + service);
  } catch {
    // ignore
  }
}

export async function setGenericPassword(
  username: string,
  password: string,
  options?: SetOptions | string,
): Promise<Result> {
  const service = serviceOf(options);
  const gated = isMemoryOnly(service, options);
  if (gated) {
    memory.set(service, { username, password });
    memoryServices.add(service);
    removeSession(service);
  } else {
    memoryServices.delete(service);
    memory.delete(service);
    writeSession(service, username, password);
  }
  return { service, storage: gated ? 'memory' : 'sessionStorage' };
}

export async function getGenericPassword(
  options?: GetOptions | string,
): Promise<false | UserCredentials> {
  const service = serviceOf(options);
  if (memoryServices.has(service) || isMemoryOnly(service)) {
    const entry = memory.get(service);
    return entry ? { ...entry, service, storage: 'memory' } : false;
  }
  const entry = readSession(service);
  if (entry) return { ...entry, service, storage: 'sessionStorage' };
  if (service === DEVICE_TRUST_SERVICE) {
    return { ...DEVICE_TRUST_PLACEHOLDER, service, storage: 'sessionStorage' };
  }
  return false;
}

export async function resetGenericPassword(options?: GetOptions | string): Promise<boolean> {
  const service = serviceOf(options);
  memory.delete(service);
  memoryServices.delete(service);
  removeSession(service);
  return true;
}

export async function setInternetCredentials(
  server: string,
  username: string,
  password: string,
  options?: SetOptions,
): Promise<Result> {
  return setGenericPassword(username, password, { ...(options ?? {}), service: `inet:${server}` });
}

export async function getInternetCredentials(
  server: string,
  options?: GetOptions,
): Promise<false | UserCredentials> {
  return getGenericPassword({ ...(options ?? {}), service: `inet:${server}` });
}

export async function resetInternetCredentials(server: string): Promise<boolean> {
  return resetGenericPassword({ service: `inet:${server}` });
}

export async function hasInternetCredentials(server: string): Promise<boolean> {
  return (await getInternetCredentials(server)) !== false;
}

export async function getSupportedBiometryType(): Promise<null> {
  return null;
}

export async function getAllGenericPasswordServices(): Promise<string[]> {
  const services = new Set<string>(memoryServices);
  try {
    for (let i = 0; i < window.sessionStorage.length; i += 1) {
      const key = window.sessionStorage.key(i);
      if (key?.startsWith(SESSION_PREFIX)) services.add(key.slice(SESSION_PREFIX.length));
    }
  } catch {
    // ignore
  }
  return [...services];
}

export function canImplyAuthentication(): Promise<boolean> {
  return Promise.resolve(false);
}

export default {
  ACCESSIBLE,
  ACCESS_CONTROL,
  AUTHENTICATION_TYPE,
  STORAGE_TYPE,
  SECURITY_LEVEL,
  BIOMETRY_TYPE,
  setGenericPassword,
  getGenericPassword,
  resetGenericPassword,
  setInternetCredentials,
  getInternetCredentials,
  resetInternetCredentials,
  hasInternetCredentials,
  getSupportedBiometryType,
  getAllGenericPasswordServices,
  canImplyAuthentication,
};
